"use client";

import { useEffect, useState } from "react";
import type useLifestyleFilters from "../../hooks/useLifestyleFilters";
import { CATEGORY_LABELS, type PrizeCategory } from "../../lifestyle-constants";
import CategoryLobby from "./CategoryLobby";
import CategoryPanel from "./CategoryPanel";
import DrawerHeader from "./DrawerHeader";
import FilterChips from "./FilterChips";
import FilterScreen from "./FilterScreen";
import ResultsCta from "./ResultsCta";
import { categorySelectionCount, selectionCountFor, type DrawerRoute } from "./filterScreens";

interface FiltersDrawerProps {
  isOpen: boolean;
  filters: ReturnType<typeof useLifestyleFilters>;
  onClose: () => void;
}

/**
 * The right-hand "All filters" drawer.
 *
 * Three levels deep, as the design lays it out: the category list, one
 * category's filters, then a single filter. `route` is the whole navigation
 * state — going back is pure navigation and never clears a filter, which is
 * what RESET is for.
 */
export default function FiltersDrawer({ isOpen, filters, onClose }: FiltersDrawerProps) {
  const [route, setRoute] = useState<DrawerRoute>({ screen: "lobby" });
  const selectedCategory = filters.filters.category;

  // A category picked anywhere else — the filter bar's Categories modal, or a
  // previous visit — is where the drawer should open, rather than making the
  // user pick it again. Only on open: re-running would yank the user back
  // while they navigate.
  useEffect(() => {
    if (!isOpen) return;
    setRoute(selectedCategory ? { screen: "category", category: selectedCategory } : { screen: "lobby" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    // Stop the page behind the drawer from scrolling.
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Opening a category is also how the category filter gets set, so the counts
  // and facet lists on the screens below are already scoped to it. Re-entering
  // the category already selected must not go through setCategory, which drops
  // the makes and styles picked underneath it.
  const openCategory = (category: PrizeCategory) => {
    if (category !== selectedCategory) filters.setCategory(category);
    setRoute({ screen: "category", category });
  };

  const goBack = () => {
    if (route.screen === "filter") {
      setRoute({ screen: "category", category: route.category });
      return;
    }
    setRoute({ screen: "lobby" });
  };

  const reset = () => {
    filters.clear();
    setRoute({ screen: "lobby" });
  };

  // Titles carry their selection count, e.g. "Cars (15)" or "Ticket Price (1)",
  // so the count is visible from whichever level you are on.
  const withCount = (label: string, count: number) => (count > 0 ? `${label} (${count})` : label);

  const title =
    route.screen === "lobby"
      ? "Filters"
      : route.screen === "category"
        ? withCount(CATEGORY_LABELS[route.category], categorySelectionCount(route.category, filters))
        : withCount(route.filter.label, selectionCountFor(route.filter.key, filters));

  return (
    // The site header sits at z-[1003] and its cart/ribbon popovers just below,
    // so the drawer needs to clear all of them. z-[9999] matches the overlay
    // convention used by botb-pass-drawer.
    <div className="fixed inset-0 z-[9999] flex justify-end font-jost">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close filters"
        className="absolute inset-0 bg-black/50 cursor-default"
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Filters"
        className="relative flex flex-col w-full sm:w-[360px] lg:w-[400px] h-full bg-white shadow-xl">
        <DrawerHeader title={title} onBack={route.screen === "lobby" ? undefined : goBack} onClose={onClose} />

        {route.screen !== "lobby" && <FilterChips category={route.category} filters={filters} />}

        {route.screen === "lobby" && <CategoryLobby filters={filters} onOpenCategory={openCategory} />}

        {route.screen === "category" && (
          <CategoryPanel
            category={route.category}
            filters={filters}
            onOpenFilter={filter => setRoute({ screen: "filter", category: route.category, filter })}
          />
        )}

        {route.screen === "filter" && <FilterScreen filter={route.filter} filters={filters} />}

        <ResultsCta
          count={filters.resultCount}
          canReset={filters.isDirty}
          categoryLabel={selectedCategory ? CATEGORY_LABELS[selectedCategory] : null}
          onReset={reset}
          onApply={onClose}
        />
      </aside>
    </div>
  );
}
